import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { useCreateVisitorLabReportMutation } from "@/api/visitorLabReportApi";
import { useGetDashboardOrdersQuery } from "@/api/dashboardApi";
import { useListActiveTestsQuery } from "@/api/testApi";

interface UploadFormData {
  testOrderId: number;
  testTypeId: number;
  testName: string;
  clientId: number;
  clientName: string;
  status: string;
  resultDocumentUrl: string;
  otherDocuments: string[];
  notes: string;
}

export default function UploadVisitorLabReport() {
  const [createReport, { isLoading }] = useCreateVisitorLabReportMutation();
  const { data: orders = [] } = useGetDashboardOrdersQuery({});
  const { data: tests = [] } = useListActiveTestsQuery();

  const { register, handleSubmit, setValue, watch, reset, formState: { errors } } = useForm<UploadFormData>({
    defaultValues: {
      status: 'NOT_STARTED',
      otherDocuments: []
    }
  });

  const selectedOrderId = watch('testOrderId');
  const selectedOrder = orders.find(order => order.orderId === selectedOrderId);

  const onSubmit = async (data: UploadFormData) => {
    try {
      // If order is selected, populate client info
      if (selectedOrder) {
        data.clientId = selectedOrder.clientId;
        data.clientName = selectedOrder.clientName;
        data.testName = selectedOrder.testName;
      }

      await createReport(data).unwrap();
      reset();
      // Close dialog or show success message
    } catch (error) {
      console.error('Failed to create report:', error);
    }
  };

  const handleOrderChange = (orderId: string) => {
    const order = orders.find(o => o.orderId === parseInt(orderId));
    if (order) {
      setValue('testOrderId', order.orderId);
      setValue('clientId', order.clientId);
      setValue('clientName', order.clientName);
      setValue('testName', order.testName);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="testOrderId">Test Order</Label>
          <Select onValueChange={handleOrderChange}>
            <SelectTrigger>
              <SelectValue placeholder="Select test order" />
            </SelectTrigger>
            <SelectContent>
              {orders.map((order) => (
                <SelectItem key={order.orderId} value={order.orderId.toString()}>
                  Order #{order.orderNumber} - {order.testName} ({order.clientName})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="testTypeId">Test Type</Label>
          <Select onValueChange={(value) => setValue('testTypeId', parseInt(value))}>
            <SelectTrigger>
              <SelectValue placeholder="Select test type" />
            </SelectTrigger>
            <SelectContent>
              {tests.map((test) => (
                <SelectItem key={test.id} value={test.id.toString()}>
                  {test.testName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="clientName">Client Name</Label>
          <Input
            id="clientName"
            {...register('clientName', { required: 'Client name is required' })}
            readOnly={!!selectedOrder}
          />
          {errors.clientName && <span className="text-red-500 text-sm">{errors.clientName.message}</span>}
        </div>

        <div>
          <Label htmlFor="testName">Test Name</Label>
          <Input
            id="testName"
            {...register('testName', { required: 'Test name is required' })}
            readOnly={!!selectedOrder}
          />
          {errors.testName && <span className="text-red-500 text-sm">{errors.testName.message}</span>}
        </div>
      </div>

      <div>
        <Label htmlFor="status">Status</Label>
        <Select onValueChange={(value) => setValue('status', value)} defaultValue="NOT_STARTED">
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="NOT_STARTED">Not Started</SelectItem>
            <SelectItem value="WAITING_FOR_RESULT">Waiting For Result</SelectItem>
            <SelectItem value="READY_TO_REVIEW">Ready To Review</SelectItem>
            <SelectItem value="CANCELED">Canceled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="resultDocumentUrl">Result Document URL</Label>
        <Input
          id="resultDocumentUrl"
          {...register('resultDocumentUrl')}
          placeholder="https://example.com/document.pdf"
        />
      </div>

      <div>
        <Label htmlFor="otherDocuments">Other Documents (one URL per line)</Label>
        <Textarea
          id="otherDocuments"
          {...register('otherDocuments')}
          placeholder="https://example.com/doc1.pdf&#10;https://example.com/doc2.pdf"
          rows={3}
        />
      </div>

      <div>
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          {...register('notes')}
          placeholder="Additional notes..."
          rows={3}
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={() => reset()}>
          Reset
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? 'Creating...' : 'Create Report'}
        </Button>
      </div>
    </form>
  );
}